import { useState } from 'react'
import WorkflowSidebar from '../../../components/admin/workflow/WorkflowSidebar'
import ConfigureDataSources, { type DataSourceCard } from '../../../components/admin/workflow/ConfigureDataSources'
// import DataTrustQuality from '../../../components/admin/workflow/DataTrustQuality'
import AddCustomDataSource from '../../../components/admin/workflow/AddCustomDataSource'
import MonitorAndTrace from '../../../components/admin/workflow/MonitorAndTrace'
import ConvergedDataSection from '../../../components/admin/workflow/ConvergedDataSection'
import EnterpriseConsumptionConsole from '../../EnterpriseConsumptionConsole'
import DashboardHeader from '../../../components/admin/dashboard/DashboardHeader'
import './DashboardPage.css'
import DataTrustQuality from '@/components/admin/workflow/DataTrustQuality'

const DashboardPage = () => {
  const [selectedStep, setSelectedStep] = useState(1)
  const [showAddCustomDataSource, setShowAddCustomDataSource] = useState(false)
  const [selectedDataSource, setSelectedDataSource] = useState<DataSourceCard | undefined>(undefined)
  const [activeDataSourcesTab, setActiveDataSourcesTab] = useState<'data-sources' | 'monitor-trace'>('data-sources')
  const [monitorTraceRefreshKey, setMonitorTraceRefreshKey] = useState(0)
  const [dataHealthRefreshKey, setDataHealthRefreshKey] = useState(0)

  const workflowSteps = [
    {
      number: 1,
      title: 'Data Health',
      subtitle: 'View data quality metrics',
      active: selectedStep === 1
    },
    {
      number: 2,
      title: 'Data Sources',
      subtitle: 'View Data Sources & Priority',
      active: selectedStep === 2
    },
    {
      number: 3,
      title: 'Unified Profile View',
      subtitle: 'View Unified Client Profiles',
      active: selectedStep === 3
    },
    {
      number: 4,
      title: 'Enterprise Consumption Console',
      subtitle: 'Monitor governed data consumption',
      active: selectedStep === 4
    }
  ]

  const handleStepSelect = (step: number) => {
    setSelectedStep(step)
    setShowAddCustomDataSource(false) // Close add custom data source view when switching steps
    
    // Refresh data when navigating to Data Health
    if (step === 1) {
      setDataHealthRefreshKey(prev => prev + 1)
    }
    
    // Reset to 'data-sources' tab when navigating to step 2 (Data Sources)
    if (step === 2) {
      setActiveDataSourcesTab('data-sources')
    }
  }

  const handleShowAddCustomDataSource = () => {
    setShowAddCustomDataSource(true)
    setSelectedDataSource(undefined) // Reset selected data source when creating new one
  }

  const handleShowDataSourceDetails = (dataSource: DataSourceCard) => {
    setSelectedDataSource(dataSource)
    setShowAddCustomDataSource(true)
  }

  const handleBackToDataSources = () => {
    setShowAddCustomDataSource(false)
    setSelectedDataSource(undefined)
  }

  const renderMainContent = () => {
    // If showing add custom data source view, render it regardless of selected step
    if (showAddCustomDataSource) {
      return (
        <AddCustomDataSource 
          key={`add-datasource-${selectedDataSource?.id || 'new'}`}
          onBackToDataSources={handleBackToDataSources} 
          selectedDataSource={selectedDataSource} 
        />
      )
    }

    // Normal workflow content
    switch (selectedStep) {
      case 1:
        return <DataTrustQuality key={`data-trust-quality-${dataHealthRefreshKey}`} />
      case 2:
        return (
          <div className="data-sources-section" key={`data-sources-section-${selectedStep}`}>
            {/* Horizontal Tab Navigation */}
            <div className="horizontal-tabs-container">
              <div className="horizontal-tabs-wrapper">
                <button
                  onClick={() => setActiveDataSourcesTab('data-sources')}
                  className={`horizontal-tab-button ${activeDataSourcesTab === 'data-sources' ? 'active' : ''}`}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <ellipse cx="12" cy="5" rx="9" ry="3"/>
                    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
                    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
                  </svg>
                  Data Sources
                </button>

                <button
                  onClick={() => {
                    setActiveDataSourcesTab('monitor-trace')
                    // Increment refresh key to force MonitorAndTrace to remount and fetch fresh data
                    setMonitorTraceRefreshKey(prev => prev + 1)
                  }}
                  className={`horizontal-tab-button ${activeDataSourcesTab === 'monitor-trace' ? 'active' : ''}`}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  Monitor & Trace
                </button>

              </div>
            </div>

            {/* Tab Content */}
            <div className="horizontal-tab-content">
              {activeDataSourcesTab === 'data-sources' ? (
                <ConfigureDataSources
                  key={`configure-datasources-${activeDataSourcesTab}`}
                  onShowAddCustomDataSource={handleShowAddCustomDataSource}
                  onShowDataSourceDetails={handleShowDataSourceDetails}
                />
              ) : activeDataSourcesTab === 'monitor-trace' ? (
                <MonitorAndTrace key={`monitor-trace-${monitorTraceRefreshKey}`} />
              ) : null}
            </div>
          </div>
        )
      case 3:
        return <ConvergedDataSection key={`converged-data-section-${selectedStep}`} />
      case 4:
        return <EnterpriseConsumptionConsole key={`enterprise-consumption-${selectedStep}`} />
      default:
        return <DataTrustQuality key={`data-trust-quality-default`} />
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-100 overflow-auto z-50">
      {/* Header */}
      <DashboardHeader />

      {/* Main Content */}
      <div className="flex">
        {/* Sidebar - Configuration Workflow */}
        <WorkflowSidebar
          workflowSteps={workflowSteps}
          onStepSelect={handleStepSelect}
        />

        {/* Main Content Area - Full Width */}
        <div className="flex-1 bg-gray-100">
          {renderMainContent()}
        </div>
      </div>
    </div>
  )
}

export default DashboardPage